import { reconcileDeletedMessagePreview } from './deleted-message-preview.util';
import type { DocumentQueryParams } from '../../../types/cache/funnel-conversations/update.types';
import { type QueryClient, type QueryKey } from '@tanstack/react-query';
import {
  type FunnelConversation,
  type FunnelConversationDocument,
  type SearcherDocumentSearchFilter,
  SearcherDocumentSearchFilterOperator,
  type SearcherDocumentSearchFilterValue,
} from 'src/api/generated';
import { type QueryParams } from 'src/modules/shared/types/api/query-builder.type';
import { updateCache } from 'src/modules/shared/utils/cache/update.util';
import {
  createOptimisticFieldChecker,
  createQueryInvalidator,
  getUpdatedFields,
  invalidateNonOptimisticQueries,
  matchesFilter,
  parseFilterBy,
} from 'src/modules/shared/utils/cache/optimistic.util';
import { createOptimisticConversationDocument } from './optimistic.util';
import type { UpdateFunnelConversationContext } from '../../../types/cache/funnel-conversations/optimistic.types';

/**
 * Creates a FunnelConversation model from a FunnelConversationDocument model
 * Used primarily for error recovery when only the document model is available
 */
export const createConversationFromDocument = (
  document: FunnelConversationDocument
): FunnelConversation => {
  return {
    id: parseInt(document.id),
    tenantId: document.tenantId,
    contactId: document.contactId ? parseInt(document.contactId) : 0,
    funnelId: document.funnelId ? parseInt(document.funnelId) : 0,
    archivedAt: document.archivedAt,
    createdAt: document.createdAt,
    updatedAt: document.updatedAt,
    funnelStatusId: document.funnelStatusId
      ? parseInt(document.funnelStatusId)
      : undefined,
    userId: document.userId ?? undefined,
    propertyId:
      typeof document.propertyId === 'string'
        ? parseInt(document.propertyId)
        : document.propertyId,
  };
};

/**
 * Extracts filter information for the document cache from the query parameters
 */
const getDocumentFilterInfo = (
  matchedKey: QueryKey
): {
  hasStatusFilter: boolean;
  hasUserFilter: boolean;
  hasPropertyFilter: boolean;
  archiveFilter?: SearcherDocumentSearchFilter;
} => {
  const params =
    Array.isArray(matchedKey) && matchedKey.length > 1
      ? (matchedKey[1] as DocumentQueryParams)
      : undefined;

  if (!params?.filters) {
    return {
      hasStatusFilter: false,
      hasUserFilter: false,
      hasPropertyFilter: false,
      archiveFilter: undefined,
    };
  }

  const hasStatusFilter = params.filters.some(
    (filter: SearcherDocumentSearchFilter) => filter.field === 'funnelStatusId'
  );
  const hasUserFilter = params.filters.some(
    (filter: SearcherDocumentSearchFilter) => filter.field === 'userId'
  );
  const hasPropertyFilter = params.filters.some(
    (filter: SearcherDocumentSearchFilter) => filter.field === 'propertyId'
  );
  const archiveFilter = params.filters.find(
    (filter: SearcherDocumentSearchFilter) => filter.field === 'archivedAt'
  );

  return { hasStatusFilter, hasUserFilter, hasPropertyFilter, archiveFilter };
};

/**
 * Extracts filter information for the conversation cache from the query parameters
 */
const getConversationFilterInfo = (
  matchedKey: QueryKey
): {
  hasStatusFilter: boolean;
  hasUserFilter: boolean;
  hasPropertyFilter: boolean;
  filter: { field: string; value: string } | null;
} => {
  const params =
    Array.isArray(matchedKey) && matchedKey.length > 1
      ? (matchedKey[1] as QueryParams)
      : undefined;

  const filter = params && parseFilterBy(params.filterBy);

  if (!filter) {
    return {
      hasStatusFilter: false,
      hasUserFilter: false,
      hasPropertyFilter: false,
      filter: null,
    };
  }

  const hasStatusFilter = filter.field === 'funnelStatusId';
  const hasUserFilter = filter.field === 'userId';
  const hasPropertyFilter = filter.field === 'propertyId';

  return { hasStatusFilter, hasUserFilter, hasPropertyFilter, filter };
};

/**
 * Validates if matchedKey is valid for document queries
 */
const isValidDocumentQueryKey = (matchedKey: QueryKey): boolean => {
  const isValidMatchedKey =
    Array.isArray(matchedKey) && matchedKey.length === 2;
  if (!isValidMatchedKey) return false;

  const params = matchedKey[1] as DocumentQueryParams;
  return Array.isArray(params.filters);
};

/**
 * Validates if matchedKey is valid for conversation queries
 */
const isValidConversationQueryKey = (matchedKey: QueryKey): boolean => {
  return Array.isArray(matchedKey) && matchedKey.length === 2;
};

/**
 * Checks if document filters match the specified item
 */
const shouldAddItemForDocumentFilters = <T extends Record<string, unknown>>(
  filters: SearcherDocumentSearchFilter[],
  item: T
): boolean => {
  return filters.every((filter) => {
    if (!isOptimisticField(filter.field)) return false;

    if (
      ![
        SearcherDocumentSearchFilterOperator.EQ,
        SearcherDocumentSearchFilterOperator.IN,
        SearcherDocumentSearchFilterOperator.MISSING,
        SearcherDocumentSearchFilterOperator.EXISTS,
      ].includes(filter.operator)
    ) {
      return false;
    }

    return matchesFilter(filter, item);
  });
};

/**
 * Checks if the item should be added to unassigned filter query
 */
const shouldAddItemForUnassignedFilter = <
  T extends {
    userId?: string | number | null | undefined;
    propertyId?: string | number | null | undefined;
  },
>(
  field: string,
  value: string | undefined,
  item: T
): boolean => {
  if (field === 'userId' && !value) {
    return item.userId == null;
  }
  if (field === 'propertyId' && !value) {
    return item.propertyId == null;
  }
  return false;
};

/**
 * Checks if the funnel status has changed and requires removing the item from the cache
 */
const hasFunnelStatusChangedForRemoval = <
  T extends { funnelStatusId?: string | number | null | undefined },
>(
  hasStatusFilter: boolean,
  updatedFields: string[],
  oldItem: T,
  newItem: T
): boolean => {
  return (
    hasStatusFilter &&
    updatedFields.includes('funnelStatusId') &&
    String(oldItem.funnelStatusId) !== String(newItem.funnelStatusId)
  );
};

/**
 * Checks if the user assignment has changed and requires removing the item from the cache
 */
const hasUserChangedForRemoval = <
  T extends { userId?: string | number | null | undefined },
>(
  hasUserFilter: boolean,
  updatedFields: string[],
  oldItem: T,
  newItem: T
): boolean => {
  if (!hasUserFilter || !updatedFields.includes('userId')) {
    return false;
  }

  const wasUserAssigned = oldItem.userId != null;
  const isUserAssigned = newItem.userId != null;

  const hasUserAssignmentStatusChanged = wasUserAssigned !== isUserAssigned;
  const hasUserIdChanged = String(oldItem.userId) !== String(newItem.userId);

  return hasUserAssignmentStatusChanged || hasUserIdChanged;
};

/**
 * Checks if the property has changed and requires removing the item from the cache
 */
const hasPropertyChangedForRemoval = <
  T extends { propertyId?: string | number | null | undefined },
>(
  hasPropertyFilter: boolean,
  updatedFields: string[],
  oldItem: T,
  newItem: T
): boolean => {
  if (!hasPropertyFilter || !updatedFields.includes('propertyId')) {
    return false;
  }

  const wasPropertyAssigned = oldItem.propertyId != null;
  const isPropertyAssigned = newItem.propertyId != null;

  const hasPropertyAssignmentStatusChanged =
    wasPropertyAssigned !== isPropertyAssigned;
  const hasPropertyIdChanged =
    String(oldItem.propertyId) !== String(newItem.propertyId);

  return hasPropertyAssignmentStatusChanged || hasPropertyIdChanged;
};

const OPTIMISTIC_FIELDS = [
  'funnelStatusId',
  'userId',
  'propertyId',
  'funnelId',
  'archivedAt',
] as const;
const isOptimisticField = createOptimisticFieldChecker(OPTIMISTIC_FIELDS);
const shouldInvalidateQuery = createQueryInvalidator(isOptimisticField);

/**
 * Updates the conversation document cache.
 */
export const updateFunnelConversationDocumentCache = (
  queryClient: QueryClient,
  context: UpdateFunnelConversationContext,
  document?: FunnelConversationDocument
) => {
  let conversationDocument: FunnelConversationDocument;
  if (document) {
    conversationDocument = document;
  } else {
    conversationDocument = createOptimisticConversationDocument(
      context.params,
      context.previousConversationDocument
    );
  }

  conversationDocument = reconcileDeletedMessagePreview(
    queryClient,
    conversationDocument
  );

  const updatedFields = getUpdatedFields(context.params.data);

  invalidateNonOptimisticQueries(
    queryClient,
    ['crm/documents/funnel-conversations'],
    shouldInvalidateQuery
  );

  updateCache({
    queryClient,
    queryKey: ['crm/documents/funnel-conversations'],
    updatedData: conversationDocument,
    shouldRemoveItem: (
      matchedKey: QueryKey,
      oldItem: FunnelConversationDocument
    ) => {
      if (oldItem.id !== conversationDocument.id) return false;

      const {
        hasStatusFilter,
        hasUserFilter,
        hasPropertyFilter,
        archiveFilter,
      } = getDocumentFilterInfo(matchedKey);

      const shouldRemoveForFunnelStatusChange =
        hasFunnelStatusChangedForRemoval(
          hasStatusFilter,
          updatedFields,
          oldItem,
          conversationDocument
        );

      const shouldRemoveForUserChange = hasUserChangedForRemoval(
        hasUserFilter,
        updatedFields,
        oldItem,
        conversationDocument
      );

      const shouldRemoveForPropertyChange = hasPropertyChangedForRemoval(
        hasPropertyFilter,
        updatedFields,
        oldItem,
        conversationDocument
      );
      const shouldRemoveForArchiveChange =
        archiveFilter !== undefined &&
        updatedFields.includes('archivedAt') &&
        !matchesFilter(archiveFilter, conversationDocument);

      return (
        shouldRemoveForFunnelStatusChange ||
        shouldRemoveForUserChange ||
        shouldRemoveForPropertyChange ||
        shouldRemoveForArchiveChange
      );
    },
    shouldAddItem: (matchedKey: QueryKey) => {
      if (!isValidDocumentQueryKey(matchedKey)) return false;

      const params = matchedKey[1] as DocumentQueryParams;
      return shouldAddItemForDocumentFilters(
        params.filters!,
        conversationDocument
      );
    },
  });
};

/**
 * Updates both conversation and document caches.
 */
export const updateFunnelConversationCache = (
  queryClient: QueryClient,
  context: UpdateFunnelConversationContext,
  conversation?: FunnelConversation,
  conversationDocument?: FunnelConversationDocument
) => {
  updateFunnelConversationDocumentCache(
    queryClient,
    context,
    conversationDocument
  );

  updateFunnelConversationResourceCache(queryClient, context, conversation);
};

/** Updates relational caches without fabricating a search-enriched document. */
export const updateFunnelConversationResourceCache = (
  queryClient: QueryClient,
  context: UpdateFunnelConversationContext,
  conversation?: FunnelConversation
): void => {
  if (!conversation) return;

  const updatedFields = getUpdatedFields(context.params.data);

  invalidateNonOptimisticQueries(
    queryClient,
    ['crm/funnel-conversations'],
    shouldInvalidateQuery
  );

  updateCache({
    queryClient,
    queryKey: ['crm/funnel-conversations'],
    updatedData: conversation,
    shouldRemoveItem: (matchedKey: QueryKey, oldItem: FunnelConversation) => {
      if (oldItem.id !== conversation.id) return false;

      const { hasStatusFilter, hasUserFilter, hasPropertyFilter, filter } =
        getConversationFilterInfo(matchedKey);

      if (!filter) return false;

      const shouldRemoveForFunnelStatusChange =
        hasFunnelStatusChangedForRemoval(
          hasStatusFilter,
          updatedFields,
          oldItem,
          conversation
        );

      const shouldRemoveForUserChange = hasUserChangedForRemoval(
        hasUserFilter,
        updatedFields,
        oldItem,
        conversation
      );

      const shouldRemoveForPropertyChange = hasPropertyChangedForRemoval(
        hasPropertyFilter,
        updatedFields,
        oldItem,
        conversation
      );

      return (
        shouldRemoveForFunnelStatusChange ||
        shouldRemoveForUserChange ||
        shouldRemoveForPropertyChange
      );
    },
    shouldAddItem: (matchedKey: QueryKey) => {
      if (!isValidConversationQueryKey(matchedKey)) return false;

      const params = matchedKey[1] as QueryParams;
      const filter = parseFilterBy(params.filterBy);

      if (!filter) return true;

      if (!isOptimisticField(filter.field)) return false;

      if (
        shouldAddItemForUnassignedFilter(
          filter.field,
          filter.value,
          conversation
        )
      ) {
        return true;
      }

      const simpleFilterBySearchFilter: SearcherDocumentSearchFilter = {
        field: filter.field,
        operator: SearcherDocumentSearchFilterOperator.EQ,
        value: filter.value as SearcherDocumentSearchFilterValue,
      };

      return matchesFilter(simpleFilterBySearchFilter, conversation);
    },
  });

  updateCache({
    queryClient,
    queryKey: ['crm/funnel-conversations', context.params.id],
    updatedData: conversation,
    exact: true,
  });
};
